import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDateTime } from "@/lib/utils-ui";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";

const STATUSES = ["open","assigned","in_progress","resolved","closed"];
const STATUS_LABEL = { open: "Open", assigned: "Assigned", in_progress: "In Progress", resolved: "Resolved", closed: "Closed" };
const CATEGORIES = ["it_issue","asset_request","repair_request","toner_request","access_request","other"];
const PRIORITIES = ["low","medium","high","critical"];

export default function TicketsPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "it_issue", priority: "medium", company_id: "" });
  const [companies, setCompanies] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/tickets");
    setTickets(data);
  };
  useEffect(() => { load(); api.get("/companies").then(r => setCompanies(r.data)); }, []);

  const create = async () => {
    try {
      const payload = { ...form };
      if (user.role !== "super_admin") delete payload.company_id;
      await api.post("/tickets", payload);
      toast.success("Ticket created"); setOpen(false); load();
      setForm({ title: "", description: "", category: "it_issue", priority: "medium", company_id: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const byStatus = (s) => tickets.filter(t => t.status === s);

  return (
    <div className="p-8 space-y-6" data-testid="tickets-page">
      <div className="flex items-start justify-between">
        <div>
          <DataLabel>Module 05</DataLabel>
          <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">Support Desk</h1>
          <p className="text-sm text-muted-foreground mt-1">{tickets.length} tickets · AI-assisted triage via GPT-5.2</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu module="tickets" />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-ticket-btn" className="bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head"><Plus size={14} className="mr-1"/>New Ticket</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-head uppercase tracking-tight">Raise Ticket</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {user.role === "super_admin" && (
                <div><Label className="data-label">Company</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Select"/></SelectTrigger>
                    <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label className="data-label">Title</Label><Input data-testid="ticket-form-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})}/></div>
              <div><Label className="data-label">Description</Label><Textarea data-testid="ticket-form-desc" rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})}/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="data-label">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="ticket-form-category"><SelectValue/></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace("_"," ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="data-label">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger data-testid="ticket-form-priority"><SelectValue/></SelectTrigger>
                    <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="ticket-form-submit" onClick={create} className="bg-black text-white hover:bg-[#FF5000]">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Kanban */}
      <div className="border border-border bg-white overflow-x-auto" data-testid="kanban-board">
        <div className="grid grid-cols-5 min-w-[1200px]">
          {STATUSES.map(s => (
            <div key={s} className="kanban-col p-3" data-testid={`kanban-col-${s}`}>
              <div className="flex items-center justify-between pb-3 border-b border-border mb-3">
                <div className="font-head font-bold uppercase text-xs tracking-wider">{STATUS_LABEL[s]}</div>
                <div className="font-mono text-xs text-muted-foreground">{byStatus(s).length}</div>
              </div>
              <div className="space-y-3">
                {byStatus(s).map(t => (
                  <button key={t.id} onClick={() => nav(`/tickets/${t.id}`)} data-testid={`ticket-card-${t.id}`}
                          className={`w-full text-left border border-border bg-white p-3 hover:bg-muted sla-${s}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[10px] text-muted-foreground">{t.ticket_id}</div>
                      <span className={`text-[10px] uppercase px-1.5 py-0.5 font-semibold priority-${t.priority}`}>{t.priority}</span>
                    </div>
                    <div className="font-semibold text-sm mt-1 line-clamp-2">{t.title}</div>
                    <div className="data-label mt-2">{t.category.replace("_"," ")}</div>
                    {t.ai_suggestion && <div className="mt-2 text-[10px] text-[#FF5000] uppercase tracking-wider">AI · Analyzed</div>}
                  </button>
                ))}
                {byStatus(s).length === 0 && <div className="text-xs text-muted-foreground py-6 text-center">—</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
